import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import logoBlue from '../../../../assets/images/logo_blue.png';

// ========================================
// Styled Components - Matching Legacy FE
// ========================================

const SidebarContainer = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 200px;
  background: #2d2d2d;
  transform: translateX(${({ isOpen }) => isOpen ? '0' : '-200px'});
  transition: transform 0.3s ease;
  z-index: 1000;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #2d2d2d;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #555;
    border-radius: 3px;
  }
`;

const SidebarOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  opacity: ${({ isOpen }) => isOpen ? 1 : 0};
  visibility: ${({ isOpen }) => isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  z-index: 999;
`;

const LogoLink = styled(NavLink)`
  display: block;
  padding: 16px 20px;
  text-decoration: none;
`;

const LogoImage = styled.img`
  height: 24px;
  width: auto;
`;

const NavItem = styled(NavLink)`
  display: block;
  padding: 12px 20px;
  color: #b8b8b8;
  font-size: 14px;
  text-decoration: none;
  transition: background 0.2s ease, color 0.2s ease;
  
  &:hover {
    background: #3d3d3d;
    color: #fff;
  }
  
  &.active {
    background: #3d3d3d;
    color: #fff;
    border-left: 3px solid #00a6c2;
    padding-left: 17px;
  }
`;

const ExternalLink = styled.a`
  display: block;
  padding: 12px 20px;
  color: #b8b8b8;
  font-size: 14px;
  text-decoration: none;
  transition: background 0.2s ease, color 0.2s ease;
  
  &:hover {
    background: #3d3d3d;
    color: #fff;
  }
`;

// ========================================
// Admin Routes Configuration
// ========================================

interface AdminRoute {
  path: string;
  label: string;
  external?: boolean;
}

function getStrategyStorybookUrl(): string {
  const isProd = window.location.hostname.endsWith('contently.com');
  return isProd
    ? 'http://strategist-storybook.prod.contently.systems/'
    : 'http://strategist-storybook.staging.contently.systems/';
}

const ADMIN_ROUTES: AdminRoute[] = [
  { path: '/admin2/search', label: 'Search' },
  { path: '/admin2/audit_events', label: 'Audit Events' },
  { path: '/admin2/behance_fields', label: 'Behance Fields' },
  { path: '/admin2/betas', label: 'Beta' },
  { path: '/admin2/clip_publications', label: 'Clip publications' },
  { path: '/admin2/content_strategies', label: 'Content strategies' },
  { path: '/admin2/external_invoices', label: 'External invoices' },
  { path: '/admin2/features', label: 'Features' },
  { path: '/admin2/metrics', label: 'Metrics' },
  { path: '/admin2/accounts', label: 'Organizations' },
  { path: '/admin2/publications', label: 'Publications' },
  { path: '/admin2/skills', label: 'Skills' },
  { path: '/admin2/tag_migrations', label: 'Tag migration' },
  { path: '/admin2/topics', label: 'Topics' },
  { path: '/admin2/transactions', label: 'Transactions' },
  { path: '/admin2/users', label: 'Users' },
  { path: getStrategyStorybookUrl(), label: 'Strategy Storybook (VPN Required)', external: true },
  { path: '/admin2/portfolio_spam_appeals/new', label: 'Portfolio spam' },
  { path: '/admin2/story_imports/new', label: 'Import Stories' },
];

// ========================================
// Component
// ========================================

interface AdminSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  
  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <>
      <SidebarOverlay isOpen={isOpen} onClick={onClose} />
      <SidebarContainer isOpen={isOpen}>
        <LogoLink to="/tasks" onClick={onClose}>
          <LogoImage src={logoBlue} alt="Contently" />
        </LogoLink>
        
        {ADMIN_ROUTES.map((route) => (
          route.external ? (
            <ExternalLink
              key={route.path}
              href={route.path}
              target="_blank"
              rel="noopener noreferrer"
            >
              {route.label}
            </ExternalLink>
          ) : (
            <NavItem
              key={route.path}
              to={route.path}
              className={isActive(route.path) ? 'active' : ''}
              onClick={onClose}
            >
              {route.label}
            </NavItem>
          )
        ))}
      </SidebarContainer>
    </>
  );
};

export default AdminSidebar;
